(ns app.front.view.admin.tenant-management.subs
  "Subscriptions and data loading for the tenant management views. The detail
  subs read only the entity-db, whichever provider implements the tenant."
  (:require [app.domain.group.model :as group-model]
            [app.external-module.model :as external-model]
            [app.front.entities :as entities]
            [app.front.view.admin.domain-groups.municipality-groups :as mg]
            [app.front.view.admin.domain-groups.subs :as domain-groups-subs]
            [app.front.view.admin.tenant-management.common :as tm-common]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.href :as href]
            [re-frame.core-instrumented :as rf]))

;; ---------------------------------------------------------------------------
;; Loading: the list gets flat summaries from `:admin/tenants`, and a tenant's
;; groups, scopes and municipalities load from `:admin/tenant-entities` when its
;; page opens. Both store the platform configuration under the
;; municipality-groups keys, so the subs reading it work from either view.
;; ---------------------------------------------------------------------------

(defn- config-db [db {:keys [categories countries timezones currencies external-modules]}]
  (assoc db
         ::mg/categories categories
         ::mg/countries countries
         ::mg/timezones timezones
         ::mg/currencies currencies
         ;; The external systems this installation can license products from;
         ;; absent with the flag off.
         ::external-modules external-modules))

;; Clears the loading state however a fetch ends: `:finally-dispatch` follows
;; every response, `:on-error` an unreachable backend (replacing its toast).
(rf/reg-event-db ::loading-done
  (fn [db _] (dissoc db ::loading?)))

(rf/reg-event-fx ::loading-failed
  (fn [_ [_ on-loaded]]
    {:fx [[:dispatch [::loading-done]]
          [:dispatch [:notifications/add :toast :error
                      :error/connection-error-title :error/connection-error-description]]
          (when on-loaded [:dispatch on-loaded])]}))

(rf/reg-event-fx ::fetch-tenants
  (fn [{:keys [db]} _]
    {:db           (assoc db ::loading? true)
     :comm/command {:command          :admin/tenants
                    :method           :get
                    :on-success       [::fetch-tenants.success]
                    :on-error         [::loading-failed nil]
                    :finally-dispatch [::loading-done]}}))

(rf/reg-event-fx ::fetch-tenants.success
  (fn [{:keys [db]} [_ _status response]]
    {:db (-> db
             (config-db response)
             (assoc ::summaries (:tenants response))
             (dissoc ::loading?))}))

(defn- fetch-tenant-fx [db tenant-id on-loaded]
  {:db           (assoc db ::loading? true)
   :comm/command {:command          :admin/tenant-entities
                  :method           :get
                  :data             {:domain-group-id tenant-id}
                  :on-success       [::fetch-tenant.success]
                  :on-error         [::loading-failed on-loaded]
                  :finally-dispatch [::fetch-tenant.done on-loaded]}})

(rf/reg-event-fx ::fetch-tenant
  (fn [{:keys [db]} [_ tenant-id]]
    (fetch-tenant-fx db tenant-id nil)))

(rf/reg-event-fx ::fetch-tenant-then
  ;; `on-loaded` is dispatched once the fetch has ended, its entities merged
  ;; when it succeeded.
  (fn [{:keys [db]} [_ tenant-id on-loaded]]
    (fetch-tenant-fx db tenant-id on-loaded)))

(rf/reg-event-fx ::fetch-tenant.done
  ;; Runs after the success handler, which queues its merge ahead of `on-loaded`.
  (fn [{:keys [db]} [_ on-loaded]]
    {:db (dissoc db ::loading?)
     :fx [(when on-loaded [:dispatch on-loaded])]}))

(rf/reg-event-fx ::fetch-tenant.success
  (fn [{:keys [db]} [_ status response]]
    {:db         (-> db (config-db response) (dissoc ::loading?))
     :dispatch-n [[::entities/merge-entity-db status (:entities response)]
                  ;; Municipality centre coordinates for the map pickers.
                  [::domain-groups-subs/fetch-municipalities-public]]}))

(defn- tenant-region
  "Derives the country + subdivision keywords for a domain group from the
  municipalities in its authority groups' scopes."
  [authority-groups]
  (let [municipalities (->> authority-groups
                            (mapcat group-model/scope-municipalities)
                            distinct)]
    {:country     (some :municipality/country municipalities)
     :subdivision (some :municipality/subdivision municipalities)}))

(defn domain-group->summary
  "Turns a domain-group entity into a flat map used by the tenant list.
  Keeps the original entity under `:group` for the detail view."
  [domain-group]
  (let [authority-groups      (->> domain-group
                                   group-model/descendant-authority-groups
                                   (sort-by :group/name))
        scopes                (mapcat group-model/scopes authority-groups)
        {:keys [country
                subdivision]} (tenant-region authority-groups)]
    {:db/id            (:db/id domain-group)
     :name             (:group/name domain-group)
     :code             (:group/id domain-group)
     :domain           (:group/domain domain-group)
     :country          country
     :subdivision      subdivision
     ;; Only primary permit types are products; supplemental ones are derived.
     :permit-types     (set (tm-common/products (group-model/get-permit-types domain-group)))
     ;; Systems behind the tenant's enabled external products.
     :external-modules (->> (external-model/module-groups domain-group)
                            (filter external-model/enabled?)
                            (map :group/external-system)
                            set)
     :authority-count  (count authority-groups)
     ;; A domain group with no active scopes has been deactivated.
     :active?          (boolean (seq scopes))
     :group            domain-group}))

(rf/reg-sub ::tenants :-> (comp vec ::summaries))

(defn- matches-query? [q tenant]
  (or (str/blank? q)
      (some (fn [v] (and v (str/includes? (str/lower-case v) q)))
            [(:name tenant)
             (:code tenant)
             (some-> tenant :subdivision name)])))

(rf/reg-sub ::filtered-tenants
  :<- [::tenants]
  (fn [tenants [_ query]]
    (let [q (some-> query str/trim str/lower-case)]
      (if (str/blank? q)
        tenants
        (filterv (partial matches-query? q) tenants)))))

(rf/reg-sub ::tenant
  (fn [[_ tenant-id]]
    (rf/subscribe [::entities/entity tenant-id]))
  (fn [domain-group _]
    (when domain-group
      (domain-group->summary domain-group))))

;; The languages the tenant's strings may be edited in: its `:group/languages`,
;; or the default language. Zero-arg and read from the route, so it can serve as
;; the `:allowed-locales-sub` of `app.front.ui.i18n` components.
(rf/reg-sub ::tenant-languages
  :<- [:route/match]
  :<- [::entities/context]
  (fn [[match ctx] _]
    (-> (entities/find-entity ctx (-> match :parameters :path :tenant-id))
        i18n-model/group-languages)))

(rf/reg-sub ::loading? :-> (comp boolean ::loading?))

(rf/reg-sub ::detail-view-data
  ;; `:view-sub` for the detail route: sends the sub-header's Back button to the
  ;; tenant list rather than the default dashboard.
  :<- [::href/branded-href :view/admin.tenant-management]
  (fn [back-href _]
    {:back-href back-href}))
